import { useRef, type CSSProperties, type KeyboardEvent } from "react";
import { useEmbedTokens } from "../theme";

export interface SegmentedOption { id: string; label: string; count?: number | string }

interface Props {
  "aria-label": string;
  options: SegmentedOption[];
  value: string;
  onChange: (id: string) => void;
  /**
   * Unit phrase for the screen-reader count, for example `(n) => `${n} orders``.
   * Without it the count is announced bare ("Open 2"); with it the name reads
   * "Open, 2 orders".
   */
  countLabel?: (count: number | string) => string;
}

/**
 * A single-select filter: a radiogroup of pills with optional counts, used for
 * filtering one list (not for switching between pages; that is EmbedTabs).
 * Arrow keys move the selection, Home/End jump, and only the selected radio is
 * a tab stop. A `value` naming no option clamps the tab stop to the first
 * option so the group always has exactly one. It scrolls horizontally when
 * narrow rather than wrapping.
 */
export function EmbedSegmented({ options, value, onChange, countLabel, ...rest }: Props) {
  const t = useEmbedTokens();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const activeIdx = options.findIndex((o) => o.id === value);
  const tabStop = activeIdx === -1 ? 0 : activeIdx;

  const select = (id: string) => { refs.current[id]?.focus(); onChange(id); };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const n = options.length;
    if (n === 0) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); select(options[(tabStop + 1) % n].id); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); select(options[(tabStop - 1 + n) % n].id); }
    else if (e.key === "Home") { e.preventDefault(); select(options[0].id); }
    else if (e.key === "End") { e.preventDefault(); select(options[n - 1].id); }
  };

  const groupStyle: CSSProperties = {
    display: "flex", gap: t.spacing.xs, overflowX: "auto", maxWidth: "100%",
    fontFamily: t.fontFamily, padding: 2,
  };
  const itemStyle = (active: boolean): CSSProperties => ({
    fontFamily: "inherit", fontSize: t.fontSize.sm,
    fontWeight: active ? t.fontWeight.medium : t.fontWeight.regular,
    minHeight: t.buttonHeight, padding: `0 ${t.spacing.md}px`,
    borderRadius: t.borderRadiusControl, cursor: "pointer", flex: "0 0 auto",
    whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6,
    // TD-575 (Task 12): the control border (3:1 non-text), not the 1.26:1 divider tone.
    border: `1px solid ${active ? "transparent" : t.colors.border}`,
    background: active ? t.colors.primaryTint : t.colors.surface,
    color: active ? t.colors.link : t.colors.textSecondary,
  });

  return (
    <div role="radiogroup" aria-label={rest["aria-label"]} style={groupStyle}>
      {options.map((o, i) => {
        const active = i === activeIdx;
        return (
          <button
            type="button" key={o.id} role="radio" aria-checked={active}
            // An explicit name instead of a visually-hidden count: an
            // absolutely positioned hidden span is blockified and the browser
            // inserts a space before it ("Open , 2 orders").
            aria-label={o.count === undefined ? undefined : countLabel ? `${o.label}, ${countLabel(o.count)}` : `${o.label} ${o.count}`}
            tabIndex={i === tabStop ? 0 : -1}
            ref={(el) => { refs.current[o.id] = el; }}
            style={itemStyle(active)}
            onClick={() => onChange(o.id)} onKeyDown={onKeyDown}
          >
            <span>{o.label}</span>
            {o.count !== undefined && (
              <span aria-hidden="true" style={{ fontVariantNumeric: "tabular-nums", opacity: 0.85 }}>{o.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
